// src/pages/Utilisateurs.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import { getActiveToken, getActiveUserRaw, getActiveAuthRole } from '../utils/authStorage';
import {
  FaPlus,
  FaToggleOn,
  FaToggleOff,
  FaUser,
  FaCircle,
  FaTimes,
  FaSpinner,
  FaSync,
  FaChevronLeft,
  FaChevronRight,
  FaTrash,
  FaEdit,
  FaUserTie,
  FaArrowLeft,
  FaStore
} from 'react-icons/fa';

const API_BASE = process.env.REACT_APP_AUTH_SERVICE_URL || 'http://localhost:3000';
const STORE_API = process.env.REACT_APP_STORE_SERVICE_URL || 'http://localhost:3006';

const formAccentBtnClass = 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg hover:from-blue-700 hover:to-blue-800 transition-all';
const formInputClass = 'w-full rounded-xl border border-slate-200/90 bg-slate-50/50 px-3.5 py-2.5 text-sm text-slate-800 shadow-[inset_0_1px_2px_rgba(15,23,42,0.03)] transition duration-200 placeholder:text-slate-400 hover:border-slate-300 hover:bg-white focus:border-blue-600 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-600/15';
const formLabelClass = 'mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400';

function UserFormDivider({ label }) {
  return (
    <div className="col-span-full flex items-center gap-3 pt-1">
      <span className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.14em] text-blue-700">{label}</span>
      <div className="h-px flex-1 bg-slate-200" />
    </div>
  );
}

function UserFormField({ label, required, className = '', children }) {
  return (
    <div className={className}>
      <label className={formLabelClass}>
        {label}{required ? <span className="text-blue-600"> *</span> : ''}
      </label>
      {children}
    </div>
  );
}

function UserFormShell({
  title,
  subtitle,
  formId,
  onSubmit,
  onBack,
  onCancel,
  submitting,
  submitLabel,
  SubmitIcon,
  children,
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="shrink-0 border-b bg-white shadow-sm">
        <div className="flex flex-col gap-3 p-3 sm:flex-row sm:items-center sm:justify-between sm:p-4 lg:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-r from-blue-600 to-blue-700">
              <FaUser className="text-sm text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="truncate text-lg font-bold text-gray-900 sm:text-xl">{title}</h1>
              {subtitle && <p className="truncate text-xs text-gray-500">{subtitle}</p>}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={onBack}
              disabled={submitting}
              className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 sm:text-sm"
            >
              <FaArrowLeft className="text-xs" />
              Retour
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              className="rounded-lg border px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 sm:text-sm"
            >
              Annuler
            </button>
            <button
              type="submit"
              form={formId}
              disabled={submitting}
              className={`inline-flex items-center rounded-lg px-3 py-2 text-xs font-medium disabled:opacity-50 sm:text-sm ${formAccentBtnClass}`}
            >
              {submitting ? <FaSpinner className="mr-1.5 animate-spin" /> : SubmitIcon ? <SubmitIcon className="mr-1.5 text-xs" /> : null}
              {submitLabel}
            </button>
          </div>
        </div>
      </div>

      <form id={formId} onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="min-h-0 flex-1 overflow-hidden p-3 sm:p-4 lg:p-6">
          <div className="h-full rounded-xl border border-slate-200/80 bg-white p-5 shadow-sm ring-1 ring-slate-900/5 sm:p-6 lg:p-8">
            {children}
          </div>
        </div>
      </form>
    </div>
  );
}

export default function Utilisateurs() {
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    name: '',
    lastName: '',
    role: 'user',
    active: true,
    storeId: '',
    merchantName: ''
  });

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 7;

  const [globalStats, setGlobalStats] = useState({
    total: 0,
    totalActive: 0,
    totalInactive: 0
  });

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  // Stores
  const [stores, setStores] = useState([]);
  const [storesLoading, setStoresLoading] = useState(false);
  const [storesError, setStoresError] = useState(null);

  // current logged user (from local/session storage)
  const [currentUser, setCurrentUser] = useState(null);

  // auth helpers
  const getToken = () => getActiveToken() || null;
  const authHeaders = () => {
    const token = getToken();
    const base = { 'Content-Type': 'application/json' };
    if (token) return { ...base, Authorization: `Bearer ${token}` };
    return base;
  };

  const getCurrentUser = () => {
    try {
      const raw = getActiveUserRaw() || null;
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  };

  const handleAuthError = async (res) => {
    let body = null;
    try { body = await res.json(); } catch (e) { /* ignore */ }
    const msg = (body && body.message) ? body.message.toLowerCase() : '';
    if (res.status === 401 || msg.includes('token') || msg.includes('authorization')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
      navigate('/login');
      throw new Error(body?.message || 'Authentification requise');
    }
    throw new Error(body?.message || `Erreur ${res.status}`);
  };

  // Fetch users
  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    const token = getToken();
    if (!token) {
      setLoading(false);
      navigate('/login');
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/users`, { headers: authHeaders() });
      if (!res.ok) await handleAuthError(res);
      const data = await res.json();
      const list = Array.isArray(data) ? data : (data.users || []);
      setUsers(list);
      computeStats(list);
      setCurrentPage(1);
    } catch (err) {
      setError(err.message || 'Erreur récupération utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  const computeStats = (list) => {
    const total = Array.isArray(list) ? list.length : 0;
    const totalActive = list.filter(u => u.active === true).length;
    const totalInactive = total - totalActive;
    setGlobalStats({ total, totalActive, totalInactive });
  };

  useEffect(() => {
    const cu = getCurrentUser();
    setCurrentUser(cu);
    if (getActiveAuthRole() === 'merchant') {
      navigate('/dashboard', { replace: true });
      return;
    }
    fetchUsers();
    fetchStores();
  }, []);

  // Fetch stores for dropdown + resolving
  const fetchStores = async () => {
    setStoresLoading(true);
    setStoresError(null);
    try {
      const headers = authHeaders();
      if (!headers) { navigate('/login'); return; }
      const res = await fetch(`${STORE_API.replace(/\/$/, '')}/stores`, { headers });
      if (!res.ok) await handleAuthError(res);
      const data = await res.json();
      const list = Array.isArray(data.stores) ? data.stores : (Array.isArray(data) ? data : (data.stores || []));
      setStores(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('fetchStores error', err);
      setStores([]);
      setStoresError(err.message || String(err));
    } finally {
      setStoresLoading(false);
    }
  };

  // Resolve store info for a user:
  const resolveStore = (user) => {
    if (!user) return null;
    // If storeId already populated as object
    if (user.storeId && typeof user.storeId === 'object') {
      return user.storeId;
    }
    // If user has a store object under another key (rare)
    if (user.store && typeof user.store === 'object') {
      return user.store;
    }
    // If storeId is a string (ObjectId), lookup in stores list
    const id = user.storeId ? String(user.storeId) : (user.storeId === 0 ? String(user.storeId) : null);
    if (!id) return null;
    return stores.find(s => String(s._id || s.id) === id) || null;
  };

  // --- HELPERS pour filtrer magasins sans manager / normaliser storeId depuis user ---
  const getStoreIdFromUser = (u) => {
    if (!u) return null;
    if (u.storeId && typeof u.storeId !== 'object') return String(u.storeId);
    if (u.storeId && typeof u.storeId === 'object') return String(u.storeId._id || u.storeId.id || '');
    if (u.store && typeof u.store === 'object') return String(u.store.id || u.store._id || '');
    return null;
  };

  const storeHasManager = (storeId) => {
    if (!storeId) return false;
    const sid = String(storeId);
    return users.some(u => ((u.role || '').toLowerCase() === 'manager') && getStoreIdFromUser(u) === sid);
  };

  const getAvailableStores = () => {
    const list = Array.isArray(stores) ? stores : [];
    const creatingManagerByAdmin = !editingUser && ((currentUser?.role || '').toLowerCase() === 'admin') && (newUser.role === 'manager');
    const editingUserStoreId = editingUser ? getStoreIdFromUser(editingUser) : null;

    return list.filter(s => {
      const id = String(s._id || s.id || '');
      if (!id) return false;
      if (creatingManagerByAdmin) {
        // dans le cas création par admin d'un manager: n'afficher que magasins sans manager
        return !storeHasManager(id);
      }
      // si on édite un manager, laisser son magasin (même s'il est déjà marqué comme ayant un manager)
      if (editingUser && (editingUser.role || '').toLowerCase() === 'manager') {
        if (String(editingUserStoreId) === id) return true;
      }
      return true;
    });
  };
  // --- FIN HELPERS ---

  const isAdminUser = (u) => ((u?.role || '').toLowerCase() === 'admin');
  const getRoleLabel = (role) => {
    const normalized = String(role || '').toLowerCase();
    if (normalized === 'merchant') return 'Marchand';
    if (normalized === 'manager') return 'Manager';
    if (normalized === 'marketing') return 'Marketing';
    if (normalized === 'finance') return 'Finance';
    if (normalized === 'admin') return 'Admin';
    if (normalized === 'user') return 'User';
    return role || '—';
  };

  // Helpers: determine current user role states
  const cuRole = (currentUser?.role || '').toLowerCase();
  const isManager = cuRole === 'manager';
  const isAdmin = cuRole === 'admin';

  // helper pour savoir si l'utilisateur listé est le même que l'utilisateur connecté
  const isSelf = (u) => {
    if (!currentUser || !u) return false;
    return String(currentUser._id || currentUser.id) === String(u._id || u.id);
  };

  // helper by id (utile pour toggle/delete)
  const isSelfId = (id) => {
    if (!currentUser || !id) return false;
    return String(currentUser._id || currentUser.id) === String(id);
  };

  const toggleActive = async (id, currentlyActive) => {
    // sécurité frontend : un manager ne peut pas désactiver son propre compte
    if (isManager && isSelfId(id)) {
      alert("Vous ne pouvez pas désactiver votre propre compte en tant que manager.");
      return;
    }

    const endpoint = `${API_BASE}/users/${id}/${currentlyActive ? 'deactivate' : 'activate'}`;
    setIsUpdating(id);
    const token = getToken();
    if (!token) { navigate('/login'); return; }
    try {
      const res = await fetch(endpoint, { method: 'PATCH', headers: authHeaders() });
      if (!res.ok) await handleAuthError(res);
      await res.json().catch(() => ({}));
      setUsers(prev => prev.map(u => (u._id === id || u.id === id ? { ...u, active: !currentlyActive } : u)));
      setGlobalStats(prev => ({
        ...prev,
        totalActive: currentlyActive ? prev.totalActive - 1 : prev.totalActive + 1,
        totalInactive: currentlyActive ? prev.totalInactive + 1 : prev.totalInactive - 1
      }));
    } catch (err) {
      alert(`Erreur : ${err.message}`);
    } finally {
      setIsUpdating(null);
    }
  };

  // UI helpers for roles options and filters
  const getRoleOptions = () => {
    // Creation mode (editingUser == null): never include 'admin' in creation select (per requirement)
    if (!editingUser) {
      if (isManager) {
        // manager can only create 'user'
        return [{ value: 'user', label: 'User' }];
      }
      if (isAdmin) {
        // when connected user is admin: remove 'user' from creation options and allow manager/marketing/finance/merchant
        return [
          { value: 'manager', label: 'Manager' },
          { value: 'merchant', label: 'Marchand' },
          { value: 'marketing', label: 'Marketing' },
          { value: 'finance', label: 'Finance' }
        ];
      }
      // default creation roles (no 'admin' option)
      return [
        { value: 'user', label: 'User' },
        { value: 'manager', label: 'Manager' },
        { value: 'merchant', label: 'Marchand' },
        { value: 'marketing', label: 'Marketing' },
        { value: 'finance', label: 'Finance' }
      ];
    }

    
    const base = [
      { value: 'admin', label: 'Admin' },
      { value: 'user', label: 'User' },
      { value: 'manager', label: 'Manager' },
      { value: 'merchant', label: 'Marchand' },
      { value: 'marketing', label: 'Marketing' },
      { value: 'finance', label: 'Finance' }
    ];

    // If the connected user is admin => remove the 'user' and 'admin' roles from the edit dropdown per request
    if (isAdmin) {
      return base.filter(opt => !['user', 'admin'].includes(opt.value));
    }

    return base;
  };

  const getFilterRoleOptions = () => {
    if (isAdmin) {
      return [
        { value: 'all', label: 'Tous les rôles' },
        { value: 'admin', label: 'Admin' },
        { value: 'user', label: 'User' },
        { value: 'manager', label: 'Manager' },
        { value: 'merchant', label: 'Marchand' },
        { value: 'marketing', label: 'Marketing' },
        { value: 'finance', label: 'Finance' }
      ];
    }
    return [
      { value: 'all', label: 'Tous les rôles' },
      { value: 'admin', label: 'Admin' },
      { value: 'user', label: 'User' },
      { value: 'manager', label: 'Manager' },
      { value: 'merchant', label: 'Marchand' },
      { value: 'marketing', label: 'Marketing' },
      { value: 'finance', label: 'Finance' }
    ];
  };

  // Create user
  const handleCreate = async (e) => {
    e.preventDefault();
    const token = getToken();
    if (!token) { navigate('/login'); return; }
    const cu = currentUser;
    const creatorIsAdmin = (cu?.role || '').toLowerCase() === 'admin';
    const creatorIsManager = (cu?.role || '').toLowerCase() === 'manager';

    // Managers can only create users with role 'user'
    if (creatorIsManager && newUser.role !== 'user') {
      alert('En tant que gérant, vous ne pouvez créer que des utilisateurs avec le rôle "user".');
      return;
    }

    // If selected role is 'manager' and creator is NOT a manager, store selection is required (server also enforces)
    if (newUser.role === 'manager' && !newUser.storeId && !creatorIsManager) {
      alert('Le champ "Magasin" est obligatoire pour le rôle "manager".');
      return;
    }

    setIsSubmitting(true);
    try {
      // construire le store object si disponible dans la liste des stores
      let storeObj = null;
      if (newUser.storeId) {
        storeObj = stores.find(s => String(s._id || s.id) === String(newUser.storeId)) || null;
      } else if (creatorIsManager && cu?.storeId) {
        // si creator est manager et n'a pas fourni storeId, utiliser le store du manager
        storeObj = stores.find(s => String(s._id || s.id) === String(cu.storeId)) || null;
      }

      // payload conforme à votre API (envoie "store" comme objet si disponible)
      const payload = {
        email: newUser.email,
        name: newUser.name,
        lastName: newUser.lastName,
        role: newUser.role,
        active: newUser.active
      };
      if (newUser.password) payload.password = newUser.password;

      if (newUser.role === 'merchant') {
        payload.merchantName = `${String(newUser.name || '').trim()} ${String(newUser.lastName || '').trim()}`.trim();
      }

      if (storeObj) {
        payload.store = {
          id: storeObj._id || storeObj.id,
          code: storeObj.code,
          name: storeObj.name
        };
      } else if (creatorIsManager && cu?.storeId) {
        payload.store = { id: cu.storeId };
      }

      const res = await fetch(`${API_BASE}/users`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(payload)
      });
      if (!res.ok) await handleAuthError(res);
      await res.json().catch(() => ({}));

      // reset form + fermer modal + refresh list
      setNewUser({ email: '', password: '', name: '', lastName: '', role: isAdmin ? 'marketing' : 'user', active: true, storeId: '', merchantName: '' });
      setShowForm(false);
      fetchUsers();
    } catch (err) {
      alert(`Erreur création : ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeForm = () => {
    if (isSubmitting) return;
    setShowForm(false);
    setEditingUser(null);
  };

  const openCreateForm = () => {
    setEditingUser(null);
    setNewUser({
      email: '',
      password: '',
      name: '',
      lastName: '',
      role: isAdmin ? 'marketing' : (isManager ? 'user' : 'user'),
      active: true,
      storeId: isManager ? (currentUser?.storeId ? String(currentUser.storeId) : '') : '',
      merchantName: ''
    });
    setShowForm(true);
    fetchStores();
  };

  const openEdit = (user) => {
    // utilise le helper getStoreIdFromUser pour normaliser la valeur
    const storeIdVal = getStoreIdFromUser(user) || '';
    setEditingUser(user);
    setNewUser({
      email: user.email || '',
      password: '',
      name: user.name || '',
      lastName: user.lastName || '',
      role: user.role || 'user',
      active: typeof user.active === 'boolean' ? user.active : true,
      storeId: storeIdVal,
      merchantName: user.merchantId?.name || user.merchantName || ''
    });
    setShowForm(true);
    fetchStores();
  };

  // Edit user
  const handleEdit = async (e) => {
    e.preventDefault();
    if (!editingUser) return;

    const token = getToken();
    if (!token) {
      navigate('/login');
      return;
    }

    const cu = currentUser;

    // Déterminer le store sélectionné ou existant
    let storeObj = null;
    if (newUser.storeId) {
      storeObj = stores.find(
        (s) => String(s._id || s.id) === String(newUser.storeId)
      ) || null;
    } else {
      // essayer depuis editingUser (ex: user édité avait déjà un store)
      const editingStoreId = getStoreIdFromUser(editingUser);
      if (editingStoreId) {
        storeObj = stores.find(
          (s) => String(s._id || s.id) === String(editingStoreId)
        ) || null;
      } else if ((cu?.role || "").toLowerCase() === "manager" && cu?.storeId) {
        storeObj = stores.find(
          (s) => String(s._id || s.id) === String(cu.storeId)
        ) || null;
      }
    }

    // Vérifier si le rôle choisi est "manager"
    if ((newUser.role || "").toLowerCase() === "manager" && !storeObj) {
      alert(
        'Le champ "Magasin" est obligatoire pour le rôle "manager". Veuillez sélectionner un magasin.'
      );
      return;
    }

    setIsSubmitting(true);
    try {
      // Security: if editing an admin, force keeping their role unchanged (frontend guard)
      const isEditingAdmin = String(editingUser?.role || '').toLowerCase() === 'admin';

      const updates = {
        email: newUser.email,
        name: newUser.name,
        lastName: newUser.lastName,
        // if editing an admin, keep their role as-is
        role: isEditingAdmin ? editingUser.role : newUser.role,
        active: newUser.active,
      };

      // IMPORTANT: if the connected user is a manager, they MUST NOT be allowed to change the role
      if ((cu?.role || '').toLowerCase() === 'manager') {
        updates.role = editingUser.role; // force keep original role
      }

      if (newUser.password) updates.password = newUser.password;

      // Ajouter le store uniquement s’il existe (et ajouter storeId pour compatibilité)
      if (storeObj) {
        updates.store = {
          id: storeObj._id || storeObj.id,
          code: storeObj.code,
          name: storeObj.name,
        };
        updates.storeId = String(storeObj._id || storeObj.id);
      }

      const res = await fetch(
        `${API_BASE}/users/${editingUser._id || editingUser.id}`,
        {
          method: "PATCH",
          headers: authHeaders(),
          body: JSON.stringify(updates),
        }
      );

      if (!res.ok) await handleAuthError(res);
      await res.json().catch(() => ({}));

      setShowForm(false);
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      alert(`Erreur modification : ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (userId) => {
    // Si l'utilisateur connecté est manager => interdit de supprimer (front guard)
    if (isManager) {
      alert("En tant que manager, vous n'avez pas le droit de supprimer des utilisateurs.");
      return;
    }

    // (Garde supplémentaire si jamais) empêcher suppression de soi-même
    if (isSelfId(userId) && !(isAdmin)) {
      // si tu veux empêcher même l'admin de supprimer son propre compte, conserve cette alerte ;
      // ici on laisse l'admin supprimer (donc on ne bloque admin).
      // Ce bloc n'empêche pas l'admin par défaut.
    }

    if (!window.confirm('Supprimer cet utilisateur ? Cette action est irréversible.')) return;
    const token = getToken();
    if (!token) { navigate('/login'); return; }
    try {
      const res = await fetch(`${API_BASE}/users/${userId}`, { method: 'DELETE', headers: authHeaders() });
      if (!res.ok) await handleAuthError(res);
      await res.json().catch(() => ({}));
      setUsers(prev => prev.filter(u => !(u._id === userId || u.id === userId)));
      computeStats(users.filter(u => !(u._id === userId || u.id === userId)));
    } catch (err) {
      alert(`Erreur suppression : ${err.message}`);
    }
  };

  // filters & pagination
  const filtered = users.filter(u => {
    const q = search.trim().toLowerCase();
    const matchesSearch = !q || (u.email && u.email.toLowerCase().includes(q)) || (u.name && u.name.toLowerCase().includes(q)) || (u.lastName && u.lastName.toLowerCase().includes(q));
    const matchesRole = roleFilter === 'all' || (u.role === roleFilter);
    return matchesSearch && matchesRole;
  });
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginated = filtered.slice(startIndex, startIndex + itemsPerPage);
  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const hasActiveFilters = !!(search.trim() || roleFilter !== 'all');

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getVisiblePages = () => {
    const maxVisible = window.innerWidth < 768 ? 3 : 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  function formatDate(dateString) {
    if (!dateString) return '—';
    const d = new Date(dateString);
    if (isNaN(d)) return dateString;
    return d.toLocaleDateString('fr-FR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit'
    });
  }

  function getStatusBadge(active) {
    return active ? (
      <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200/70">
        <FaCircle className="mr-1 h-2 w-2" />
        Actif
      </span>
    ) : (
      <span className="inline-flex items-center rounded-full bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-700 ring-1 ring-rose-200/70">
        <FaCircle className="mr-1 h-2 w-2" />
        Inactif
      </span>
    );
  }

  const getRoleBadge = (role) => {
    const normalized = String(role || '').toLowerCase();
    const label = getRoleLabel(role);
    const styles = {
      admin: 'bg-violet-50 text-violet-700 ring-violet-200/70',
      manager: 'bg-blue-50 text-blue-700 ring-blue-200/70',
      merchant: 'bg-amber-50 text-amber-800 ring-amber-200/70',
      marketing: 'bg-pink-50 text-pink-700 ring-pink-200/70',
      finance: 'bg-teal-50 text-teal-700 ring-teal-200/70',
    };
    const style = styles[normalized] || 'bg-slate-100 text-slate-700 ring-slate-200/80';
    return (
      <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${style}`}>
        {label}
      </span>
    );
  };

  const renderUserFormContent = () => (
    <div className="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2 lg:grid-cols-4">
      <UserFormDivider label="Identité" />
      <UserFormField label="Email" required className="sm:col-span-2 lg:col-span-2">
        <input
          required
          type="email"
          placeholder="exemple@domaine.com"
          value={newUser.email}
          onChange={e => setNewUser({ ...newUser, email: e.target.value })}
          className={formInputClass}
        />
      </UserFormField>
      <UserFormField label={editingUser ? 'Mot de passe (vide = inchangé)' : 'Mot de passe'} required={!editingUser} className="sm:col-span-2 lg:col-span-2">
        <input
          type="password"
          placeholder={editingUser ? 'Laisser vide si inchangé' : 'Mot de passe'}
          value={newUser.password}
          onChange={e => setNewUser({ ...newUser, password: e.target.value })}
          className={formInputClass}
          required={!editingUser}
        />
      </UserFormField>
      <UserFormField label="Prénom" required>
        <input
          required
          placeholder="Prénom"
          value={newUser.name}
          onChange={e => setNewUser({ ...newUser, name: e.target.value })}
          className={formInputClass}
        />
      </UserFormField>
      <UserFormField label="Nom" required>
        <input
          required
          placeholder="Nom"
          value={newUser.lastName}
          onChange={e => setNewUser({ ...newUser, lastName: e.target.value })}
          className={formInputClass}
        />
      </UserFormField>

      <UserFormDivider label="Accès" />
      <UserFormField label="Rôle">
        {editingUser && (String(editingUser.role || '').toLowerCase() === 'admin' || isManager) ? (
          <div className={`${formInputClass} bg-slate-100 text-slate-700`}>
            {editingUser.role ? (editingUser.role.charAt(0).toUpperCase() + editingUser.role.slice(1)) : '—'}
          </div>
        ) : (
          <select
            value={newUser.role}
            onChange={e => {
              const role = e.target.value;
              setNewUser(prev => ({
                ...prev,
                role,
                storeId: role === 'manager' ? prev.storeId : '',
                merchantName: role === 'merchant' ? prev.merchantName : ''
              }));
            }}
            disabled={!editingUser && isManager}
            className={formInputClass}
          >
            {getRoleOptions().map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </select>
        )}
      </UserFormField>
      <UserFormField label="Statut">
        <select
          value={newUser.active ? 'true' : 'false'}
          onChange={e => setNewUser({ ...newUser, active: e.target.value === 'true' })}
          className={formInputClass}
        >
          <option value="true">Actif</option>
          <option value="false">Inactif</option>
        </select>
      </UserFormField>

      {newUser.role === 'manager' && (
        <>
          <UserFormDivider label="Affectation" />
          <UserFormField label="Magasin" required className="sm:col-span-2 lg:col-span-2">
            {storesLoading ? (
              <div className="flex items-center gap-2 rounded-xl border border-slate-200/90 bg-slate-50/50 px-3.5 py-2.5 text-sm text-slate-500">
                <FaSpinner className="animate-spin text-blue-600" />
                Chargement des magasins…
              </div>
            ) : storesError ? (
              <div className="rounded-xl border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-red-600">
                Impossible de récupérer les magasins : {storesError}
              </div>
            ) : (
              (() => {
                const available = getAvailableStores();
                return (
                  <>
                    <select
                      required
                      value={newUser.storeId || ''}
                      onChange={e => setNewUser({ ...newUser, storeId: e.target.value })}
                      className={formInputClass}
                    >
                      <option value="">— Sélectionner un magasin —</option>
                      {available.map(s => {
                        const id = s._id || s.id;
                        const label = `${s.code ? `${s.code} — ` : ''}${s.name || s.label || ''}`;
                        return <option key={id} value={id}>{label}</option>;
                      })}
                    </select>
                    {available.length === 0 && (
                      <p className="mt-2 text-xs text-amber-700">
                        Aucun magasin disponible — tous les magasins ont déjà un manager.
                      </p>
                    )}
                  </>
                );
              })()
            )}
          </UserFormField>
        </>
      )}

      {editingUser && String(editingUser.role || '').toLowerCase() === 'merchant' && (
        <>
          <UserFormDivider label="Marchand" />
          <UserFormField label="Enseigne" className="sm:col-span-2 lg:col-span-2">
            <div className={`${formInputClass} bg-slate-100 text-slate-700`}>
              {editingUser.merchantId?.name || editingUser.merchantName || '—'}
            </div>
          </UserFormField>
        </>
      )}
    </div>
  );

  const PaginationComponent = () => {
    if (filtered.length === 0) return null;
    const pages = getVisiblePages();
    const endIndex = Math.min(startIndex + itemsPerPage, filtered.length);

    return (
      <div className="sticky bottom-0 z-20 flex flex-col items-center justify-between gap-2 border-t border-slate-200/90 bg-white/95 px-4 py-3 backdrop-blur-sm sm:flex-row">
        <div className="order-2 text-sm text-slate-500 sm:order-1">
          Affichage de {filtered.length === 0 ? 0 : startIndex + 1} à {endIndex} sur {filtered.length}
        </div>
        <div className="order-1 flex items-center gap-1 sm:order-2">
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-600 transition hover:bg-slate-50 disabled:opacity-40"
          >
            <FaChevronLeft className="text-xs" />
          </button>
          {pages.map(p => (
            <button
              key={p}
              onClick={() => goToPage(p)}
              className={`min-w-[2rem] rounded-lg border px-2.5 py-1.5 text-sm font-medium transition ${p === currentPage ? 'border-blue-600 bg-blue-600 text-white' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}
            >
              {p}
            </button>
          ))}
          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-600 transition hover:bg-slate-50 disabled:opacity-40"
          >
            <FaChevronRight className="text-xs" />
          </button>
        </div>
      </div>
    );
  };

  // mobile card includes store info
  const UserCard = ({ user }) => {
    const admin = isAdminUser(user); // indicates whether the listed user is admin
    const store = resolveStore(user);
    const storeLabel = store ? `${store.code ? `${store.code} — ` : ''}${store.name || store.label || '—'}` : 'Siège';
    return (
      <div className="rounded-xl border border-slate-200/80 bg-white p-4 shadow-sm ring-1 ring-slate-900/5 transition hover:shadow-md">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-1 items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
              <FaUser className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-slate-900">
                {user.name} {user.lastName}
              </div>
              <div className="truncate text-xs text-slate-500">{user.email}</div>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                {getRoleBadge(user.role)}
              </div>
            </div>
          </div>
          {getStatusBadge(user.active)}
        </div>

        <div className="mb-3 grid grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Magasin</span>
            <div className="mt-1 flex items-start gap-1.5 font-medium text-slate-900">
              <FaStore className="mt-0.5 h-3 w-3 shrink-0 text-slate-400" />
              <span className="break-words">{storeLabel}</span>
            </div>
          </div>
          <div>
            <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Créé le</span>
            <div className="mt-1 font-medium tabular-nums text-slate-900">{formatDate(user.createdAt)}</div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-1 border-t border-slate-100 pt-3">
            <button
              type="button"
              onClick={() => openEdit(user)}
              title="Éditer"
              aria-label="Éditer"
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100"
            >
              <FaEdit className="h-3.5 w-3.5" />
            </button>

            {/* Activation/Désactivation: autorisée pour managers (sur les autres comptes), interdite sur les admin rows.
                Suppression: retirée pour les managers (le bouton n'apparaît pas pour un manager connecté). */}
            {!admin && !(isManager && isSelf(user)) && (
              <button
                type="button"
                onClick={() => toggleActive(user._id || user.id, user.active)}
                disabled={isUpdating === (user._id || user.id)}
                title={user.active ? 'Désactiver' : 'Activer'}
                aria-label={user.active ? 'Désactiver' : 'Activer'}
                className={`inline-flex h-8 w-8 items-center justify-center rounded-lg transition-all disabled:opacity-50 ${user.active ? 'bg-amber-50 text-amber-700 hover:bg-amber-100' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'}`}
              >
                {isUpdating === (user._id || user.id) ? <FaSpinner className="h-3.5 w-3.5 animate-spin" /> : user.active ? <FaToggleOff className="h-3.5 w-3.5" /> : <FaToggleOn className="h-3.5 w-3.5" />}
              </button>
            )}

            {/* Supprimer: seul un utilisateur non-manager (ex: admin) verra le bouton.
                On garde aussi la logique qui empêche la suppression de comptes admin (admin === listed user). */}
            {!admin && !isManager && (
              <button
                type="button"
                onClick={() => handleDelete(user._id || user.id)}
                title="Supprimer"
                aria-label="Supprimer"
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-rose-50 text-rose-700 hover:bg-rose-100"
              >
                <FaTrash className="h-3.5 w-3.5" />
              </button>
            )}
        </div>
      </div>
    );
  };

  // render
  return (
    <div className="flex h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Sidebar />
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <Navbar />

        {showForm && (
          <UserFormShell
            title={editingUser ? 'Modifier l’utilisateur' : 'Nouvel utilisateur'}
            subtitle={editingUser ? `${editingUser.name || ''} ${editingUser.lastName || ''}`.trim() || editingUser.email : 'Créez un compte et définissez ses accès'}
            formId="user-form"
            onSubmit={editingUser ? handleEdit : handleCreate}
            onBack={closeForm}
            onCancel={closeForm}
            submitting={isSubmitting}
            submitLabel={editingUser ? 'Enregistrer' : 'Créer'}
            SubmitIcon={editingUser ? FaEdit : FaPlus}
          >
            {renderUserFormContent()}
          </UserFormShell>
        )}

        {!showForm && (
        <>
        <div className="bg-white border-b shadow-sm">
          <div className="p-3 sm:p-4 lg:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 space-y-3 sm:space-y-0">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg flex items-center justify-center">
                  <FaUser className="text-white text-sm" />
                </div>
                <h1 className="text-lg sm:text-xl font-bold text-gray-900">Utilisateurs</h1>
              </div>

              <div className="flex items-center space-x-2">
                <button onClick={() => { fetchUsers(); fetchStores(); }} disabled={loading} className="flex items-center px-3 py-2 border rounded-lg text-xs font-medium hover:bg-gray-50">
                  <FaSync className={`${loading ? 'animate-spin' : 'group-hover:rotate-180'} mr-1.5 text-xs`} />
                  <span className="hidden sm:inline">Actualiser</span>
                </button>
                <button onClick={openCreateForm} className="flex items-center px-3 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all text-xs font-medium shadow-lg hover:shadow-xl">
                  <FaPlus className="mr-1.5 text-xs" />
                  <span className="hidden sm:inline">Ajouter un utilisateur</span>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
              <div className="flex min-w-0 items-center gap-2 rounded-xl border border-slate-200/80 bg-gradient-to-br from-white to-slate-50/90 px-2.5 py-2 shadow-sm ring-1 ring-slate-900/5">
                <FaUserTie className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                <div className="min-w-0">
                  <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-slate-500">Total</p>
                  <p className="text-sm font-bold tabular-nums tracking-tight text-slate-900">{globalStats.total}</p>
                </div>
              </div>

              <div className="flex min-w-0 items-center gap-2 rounded-xl border border-emerald-200/70 bg-emerald-50/50 px-2.5 py-2 shadow-sm ring-1 ring-emerald-900/5">
                <FaCircle className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
                <div className="min-w-0">
                  <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-emerald-700/90">Actifs</p>
                  <p className="text-sm font-bold tabular-nums tracking-tight text-emerald-900">{globalStats.totalActive}</p>
                </div>
              </div>

              <div className="col-span-2 flex min-w-0 items-center gap-2 rounded-xl border border-rose-200/70 bg-rose-50/50 px-2.5 py-2 shadow-sm ring-1 ring-rose-900/5 md:col-span-1">
                <FaCircle className="h-3.5 w-3.5 shrink-0 text-rose-600" />
                <div className="min-w-0">
                  <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-rose-700/90">Inactifs</p>
                  <p className="text-sm font-bold tabular-nums tracking-tight text-rose-900">{globalStats.totalInactive}</p>
                </div>
              </div>
            </div>

            {/* filters */}
            <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center space-x-2 w-full sm:w-1/2">
                <div className="relative flex-1">
                  <input placeholder="Rechercher par nom, prénom ou email..." value={search} onChange={e => setSearch(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
                </div>
                <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  {getFilterRoleOptions().map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
              </div>
            </div>
          </div>
        </div>

        <main className="flex-1 overflow-hidden p-3 sm:p-4 lg:p-6">
          {loading && (
            <div className="flex h-64 items-center justify-center px-4">
              <div className="text-center">
                <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600"></div>
                <p className="text-sm text-gray-600">Chargement des utilisateurs...</p>
              </div>
            </div>
          )}

          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-center sm:p-6">
              <FaTimes className="mx-auto mb-2 text-2xl text-red-500" />
              <p className="text-sm font-medium text-red-700">Erreur : {error}</p>
            </div>
          )}

          {!loading && !error && (
            <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-sm ring-1 ring-slate-900/5">
              <div className="hidden min-h-0 flex-1 lg:block">
                <div className="h-full overflow-auto">
                  <table className="min-w-full">
                    <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50">
                      <tr>
                        <th className="px-5 py-3.5 text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-blue-700">Utilisateur</th>
                        <th className="px-4 py-3.5 text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-blue-700">Email</th>
                        <th className="px-4 py-3.5 text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-blue-700">Rôle</th>
                        <th className="px-4 py-3.5 text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-blue-700">Magasin</th>
                        <th className="px-4 py-3.5 text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-blue-700">Statut</th>
                        <th className="px-4 py-3.5 text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-blue-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {paginated.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-6 py-14 text-center">
                            <div className="mx-auto flex max-w-sm flex-col items-center text-slate-500">
                              <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100">
                                <FaUser className="text-2xl text-slate-400" />
                              </div>
                              <p className="text-sm font-semibold text-slate-700">Aucun utilisateur trouvé</p>
                              <p className="mt-1 text-xs text-slate-500">
                                {hasActiveFilters ? 'Modifiez vos critères de recherche.' : 'Commencez par ajouter un utilisateur.'}
                              </p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        paginated.map((u, idx) => {
                          const admin = isAdminUser(u);
                          const store = resolveStore(u);
                          const storeLabel = store ? `${store.code ? `${store.code} — ` : ''}${store.name || store.label || '—'}` : (storesLoading ? 'Chargement…' : 'Siège');
                          return (
                            <tr key={u._id || u.id} className={`transition-colors hover:bg-blue-50/40 ${idx % 2 === 1 ? 'bg-slate-50/40' : ''}`}>
                              <td className="px-5 py-4 align-top">
                                <div className="flex min-w-0 items-start gap-3">
                                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                                    <FaUser className="h-3.5 w-3.5" />
                                  </div>
                                  <div className="min-w-0">
                                    <div className="truncate text-sm font-semibold text-slate-900">{u.name} {u.lastName}</div>
                                    <div className="mt-0.5 text-[11px] tabular-nums text-slate-500">Créé le {formatDate(u.createdAt)}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-4 align-top text-sm text-slate-700">
                                <div className="max-w-[16rem] truncate">{u.email}</div>
                              </td>
                              <td className="px-4 py-4 align-top whitespace-nowrap">
                                {getRoleBadge(u.role)}
                              </td>
                              <td className="px-4 py-4 align-top text-sm text-slate-600">
                                {store ? (
                                  <span className="inline-flex max-w-[14rem] items-start gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-700">
                                    <FaStore className="mt-0.5 h-3 w-3 shrink-0 text-slate-400" />
                                    <span className="whitespace-normal break-words">{storeLabel}</span>
                                  </span>
                                ) : (
                                  <span className="text-slate-400">{storeLabel}</span>
                                )}
                              </td>
                              <td className="px-4 py-4 align-top whitespace-nowrap">{getStatusBadge(u.active)}</td>
                              <td className="px-4 py-4 align-top">
                                <div className="flex items-center gap-1">
                                  <button
                                    type="button"
                                    onClick={() => openEdit(u)}
                                    title="Éditer"
                                    aria-label="Éditer"
                                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-700 transition hover:bg-blue-100"
                                  >
                                    <FaEdit className="h-3.5 w-3.5" />
                                  </button>
                                  {!admin && !(isManager && isSelf(u)) && (
                                    <button
                                      type="button"
                                      onClick={() => toggleActive(u._id || u.id, u.active)}
                                      disabled={isUpdating === (u._id || u.id)}
                                      title={u.active ? 'Désactiver' : 'Activer'}
                                      aria-label={u.active ? 'Désactiver' : 'Activer'}
                                      className={`inline-flex h-8 w-8 items-center justify-center rounded-lg transition disabled:opacity-50 ${u.active ? 'bg-amber-50 text-amber-700 hover:bg-amber-100' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'}`}
                                    >
                                      {isUpdating === (u._id || u.id) ? <FaSpinner className="h-3.5 w-3.5 animate-spin" /> : u.active ? <FaToggleOff className="h-3.5 w-3.5" /> : <FaToggleOn className="h-3.5 w-3.5" />}
                                    </button>
                                  )}
                                  {!admin && !isManager && (
                                    <button
                                      type="button"
                                      onClick={() => handleDelete(u._id || u.id)}
                                      title="Supprimer"
                                      aria-label="Supprimer"
                                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-rose-50 text-rose-700 transition hover:bg-rose-100"
                                    >
                                      <FaTrash className="h-3.5 w-3.5" />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="min-h-0 flex-1 overflow-auto p-4 lg:hidden">
                {paginated.length === 0 ? (
                  <div className="py-12 text-center">
                    <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100">
                      <FaUser className="text-2xl text-slate-400" />
                    </div>
                    <p className="text-sm font-semibold text-slate-700">Aucun utilisateur trouvé</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {paginated.map(u => <UserCard key={u._id || u.id} user={u} />)}
                  </div>
                )}
              </div>

              <PaginationComponent />
            </div>
          )}
        </main>
        </>
        )}
      </div>
    </div>
  );
}
